import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { HumanInTheLoopActionRequest } from "./human-in-the-loop";
import { PendingTranscriptToolCallsProvider } from "./HumanInTheLoopContext";

export type PendingToolReview = {
  key?: string;
  subagentId: string | undefined;
  namespace: string[];
  actions: HumanInTheLoopActionRequest[];
  action?: HumanInTheLoopActionRequest;
  decision?: "approve" | "reject" | "edit";
  agentName?: string;
  review: ReactNode;
};

const PendingToolReviewsContext = createContext<PendingToolReview[]>([]);
export const PendingToolReviewsProvider = PendingToolReviewsContext.Provider;
export const usePendingToolReviews = () =>
  useContext(PendingToolReviewsContext);

type PlacementContextValue = (
  key: string,
  element: HTMLDivElement | null
) => void;
const PlacementContext = createContext<PlacementContextValue>(() => {});

export const PendingReviewPlacement = ({
  reviewKey,
}: {
  reviewKey: string;
}) => {
  const place = useContext(PlacementContext);
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    place(reviewKey, element);
    return () => place(reviewKey, null);
  }, [element, place, reviewKey]);
  return <div ref={setElement} data-pending-review={reviewKey} />;
};

export const PendingToolReviewScope = ({
  subagentId,
  children,
}: {
  subagentId?: string;
  children?: ReactNode;
}) => {
  const reviews = usePendingToolReviews().filter(
    (review) => review.subagentId === subagentId
  );
  const [placements, setPlacements] = useState<Record<string, HTMLDivElement>>(
    {}
  );
  const place: PlacementContextValue = useCallback((key, element) => {
    setPlacements((current) => {
      if (element ? current[key] === element : !current[key]) return current;
      const next = { ...current };
      if (element) next[key] = element;
      else delete next[key];
      return next;
    });
  }, []);
  return (
    <PlacementContext.Provider value={place}>
      <PendingTranscriptToolCallsProvider
        actions={reviews.flatMap(({ actions }) => actions)}
      >
        {children}
      </PendingTranscriptToolCallsProvider>
      {reviews.some((review) => !review.decision) && (
        <div
          className="flex flex-col gap-2"
          aria-label="Outstanding tool calls"
        >
          {reviews
            .filter((item) => !item.decision)
            .map(({ review, key }) =>
              key && placements[key] ? (
                createPortal(review, placements[key], key)
              ) : (
                <span key={key}>{review}</span>
              )
            )}
        </div>
      )}
    </PlacementContext.Provider>
  );
};
